import React, { useState, useEffect } from 'react';
import { Dialog, DialogTitle, DialogContent } from '@mui/material';
import { IoIosCloseCircle } from "react-icons/io";
import { FaEdit, FaSave, FaTimes, FaIdCard, FaCalendarAlt, FaImage } from 'react-icons/fa';
import { MdLocalOffer, MdCategory } from 'react-icons/md';
import { useMyContext } from '../../../context/MyContext';
import { useForm } from 'react-hook-form';
import Swal from 'sweetalert2';
import axios from 'axios';
import { Base_Url, updateOffer } from '../../../config/config';

const ViewOffer = ({ isopen, onClose, offer }) => {
    const { isDarkMode } = useMyContext();
    const [isEditMode, setIsEditMode] = useState(false);
    const [isEditId, setIsEditId] = useState(true);
    const [imagePreview, setImagePreview] = useState(null);
    const [selectedImage, setSelectedImage] = useState(null);

    const {
        register,
        handleSubmit,
        formState: { errors },
        setValue,
        reset
    } = useForm();

    useEffect(() => {
        if (offer) {
            setValue('id', offer.id || '');
            setValue('coupon_code', offer.coupon_code || '');
            setValue('category', offer.category || '');
            setValue('discount_type', offer.discount_type || '');
            setValue('discount_value', offer.discount_value || '');
            setValue('description', offer.description || '');
            setValue('start_date', offer.start_date || '');
            setValue('end_date', offer.end_date || '');
            setValue('status', offer.status || '');
            setValue('created_at', offer.created_at || '');

            // Set image preview if exists
            if (offer.image) {
                setImagePreview(`${Base_Url}${offer.image}`);
            }
        }
    }, [offer, setValue]);

    const handleClose = () => {
        setIsEditMode(false);
        setIsEditId(true);
        setImagePreview(null);
        setSelectedImage(null);
        reset();
        onClose();
    };

    const handleEditToggle = () => {
        setIsEditMode(!isEditMode);
    };

    const handleImageChange = (e) => {
        const file = e.target.files[0];
        if (file) {
            setSelectedImage(file);
            const reader = new FileReader();
            reader.onloadend = () => {
                setImagePreview(reader.result);
            };
            reader.readAsDataURL(file);
        }
    };

    const onSubmit = async (data) => {
        const token = localStorage.getItem("token");
        const cusid = localStorage.getItem("cus_id");

        try {
            // ✅ Convert YYYY-MM-DD → DD/MM/YYYY
            const formatDate = (dateStr) => {
                if (!dateStr) return "";
                const date = new Date(dateStr);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}/${month}/${year}`;
            };

            const startDateFormatted = formatDate(data.start_date);
            const endDateFormatted = formatDate(data.end_date);

            // ✅ Prepare FormData
            const formData = new FormData();
            formData.append("category", data.category);
            formData.append("discount_type", data.discount_type);
            formData.append("discount_value", data.discount_value);
            formData.append("description", data.description);
            formData.append("start_date", startDateFormatted);
            formData.append("end_date", endDateFormatted);

            if (selectedImage) {
                formData.append("image", selectedImage);
            }

            const urlUpdate = `${Base_Url}${updateOffer}/${offer.id}`;

            // ✅ Call API using fetch()
            const response = await fetch(urlUpdate, {
                method: "POST",
                headers: {
                    "token": token,
                    "cus_id": cusid,
                },
                body: formData,
            });

            const result = await response.json();

            // ✅ Handle response
            if (result.status === "SUCCESS") {
                handleClose();

                setTimeout(() => {
                    Swal.fire({
                        title: "Success!",
                        text: result.message || "Offer updated successfully",
                        icon: "success",
                        confirmButtonColor: "#000000",
                    });
                }, 2000);

                setIsEditMode(false);
            } else {
                handleClose();
                setTimeout(() => {
                    Swal.fire({
                        title: "Error!",
                        text: result.message || "Failed to update offer",
                        icon: "error",
                        confirmButtonColor: "#000000",
                        customClass: {
                            container: "swal-high-z-index",
                        },
                    });
                }, 2000);
            }
        } catch (error) {
            handleClose();
            console.error("Error updating offer:", error);
            Swal.fire({
                title: "Error!",
                text: "An error occurred while updating the offer",
                icon: "error",
                confirmButtonColor: "#000000",
                customClass: {
                    container: "swal-high-z-index",
                },
            });
        }
    };

    const onErrors = (errors) => {
        console.log('Form Errors:', errors);
    };

    if (!offer) return null;

    const inputClass = `mt-1 p-1 text-sm border border-gray-300 dark:border-gray-700 w-full rounded focus:outline-none focus:ring-1 focus:ring-black dark:focus:ring-white focus:border-black dark:focus:border-white bg-white dark:bg-black dark:text-white disabled:bg-gray-50 dark:disabled:bg-gray-900 disabled:text-gray-500`;
    const labelClass = "block text-xs font-medium text-gray-500 dark:text-gray-400 mb-1";
    const sectionClass = "p-1 rounded border border-gray-200 dark:border-gray-800";
    const sectionTitleClass = "text-xs font-semibold mb-1.5 text-black dark:text-white flex items-center uppercase tracking-wide";

    return (
        <Dialog
            open={isopen}
            onClose={handleClose}
            maxWidth="sm"
            fullWidth
            PaperProps={{
                className: "rounded-md overflow-hidden"
            }}
        >
            <DialogTitle className="bg-emerald-500 text-white relative py-2.5 px-4">
                <h2 className="text-xs font-semibold text-white flex items-center">
                    <MdLocalOffer className="mr-2" size={14} />
                    Offer Details
                </h2>
                <IoIosCloseCircle
                    className="absolute top-2 right-3 cursor-pointer hover:opacity-70 transition-opacity"
                    size={16}
                    onClick={handleClose}
                />
            </DialogTitle>

            <DialogContent className="bg-white dark:bg-black dark:text-white custom-scrollbar " style={{ maxHeight: '75vh' }}>
                <div className="py-2">
                    <form className="space-y-3 font-sans" onSubmit={handleSubmit(onSubmit, onErrors)}>

                        {/* Primary Information Section */}
                        <div className={sectionClass}>
                            <h3 className={sectionTitleClass}>
                                <FaIdCard color='blue' className="mr-1.5" size={11} />
                                Primary Information
                            </h3>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Offer ID</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        {...register('id')}
                                        disabled={isEditId}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Coupon Code</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        {...register('coupon_code')}
                                        disabled={true}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Created At</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        {...register('created_at')}
                                        disabled={true}
                                    />
                                </div>

                                <div>
                                    <label className={labelClass}>Status</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        {...register('status')}
                                        disabled={true}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Offer Details Section */}
                        <div className={sectionClass}>
                            <h3 className={sectionTitleClass}>
                                <MdCategory color='blue' className="mr-1.5" size={12} />
                                Offer Details
                            </h3>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Category</label>
                                    <select
                                        className={inputClass}
                                        {...register('category', { required: 'Category is required' })}
                                        disabled={!isEditMode}
                                    >
                                        <option value="">Select Category</option>
                                        <option value="All">All</option>
                                        <option value="first Shipment">First Shipment</option>
                                        <option value="Returning Customer">Returning Customer</option>
                                    </select>
                                    {errors.category && <p className="text-red-500 text-xs mt-1">{errors.category.message}</p>}
                                </div>

                                <div>
                                    <label className={labelClass}>Discount Type</label>
                                    <select
                                        className={inputClass}
                                        {...register('discount_type', { required: 'Discount type is required' })}
                                        disabled={!isEditMode}
                                    >
                                        <option value="">Select Type</option>
                                        <option value="Flat">Flat</option>
                                        <option value="Percentage">Percentage</option>
                                    </select>
                                    {errors.discount_type && <p className="text-red-500 text-xs mt-1">{errors.discount_type.message}</p>}
                                </div>

                                <div>
                                    <label className={labelClass}>Discount Value</label>
                                    <input
                                        type="number"
                                        className={inputClass}
                                        {...register('discount_value', {
                                            required: 'Discount value is required',
                                            min: { value: 0, message: 'Value must be positive' }
                                        })}
                                        disabled={!isEditMode}
                                    />
                                    {errors.discount_value && <p className="text-red-500 text-xs mt-1">{errors.discount_value.message}</p>}
                                </div>

                                <div>
                                    <label className={labelClass}>Description</label>
                                    <input
                                        type="text"
                                        className={inputClass}
                                        {...register('description')}
                                        disabled={!isEditMode}
                                    />
                                </div>
                            </div>
                        </div>

                        {/* Date Information Section */}
                        <div className={sectionClass}>
                            <h3 className={sectionTitleClass}>
                                <FaCalendarAlt color='blue' className="mr-1.5" size={11} />
                                Validity Period
                            </h3>

                            <div className="grid grid-cols-2 gap-3">
                                <div>
                                    <label className={labelClass}>Start Date</label>
                                    <input
                                        type="date"
                                        className={inputClass}
                                        {...register('start_date', { required: 'Start date is required' })}
                                        disabled={!isEditMode}
                                    />
                                    {errors.start_date && <p className="text-red-500 text-xs mt-1">{errors.start_date.message}</p>}
                                </div>

                                <div>
                                    <label className={labelClass}>End Date</label>
                                    <input
                                        type="date"
                                        className={inputClass}
                                        {...register('end_date', { required: 'End date is required' })}
                                        disabled={!isEditMode}
                                    />
                                    {errors.end_date && <p className="text-red-500 text-xs mt-1">{errors.end_date.message}</p>}
                                </div>
                            </div>
                        </div>

                        {/* Image Section */}
                        <div className={sectionClass}>
                            <h3 className={sectionTitleClass}>
                                <FaImage color='blue' className="mr-1.5" size={11} />
                                Offer Image
                            </h3>

                            <div>
                                {imagePreview && (
                                    <div className="mb-3">
                                        <img
                                            src={imagePreview}
                                            alt="Offer preview"
                                            className="max-h-28 w-auto mx-auto rounded border border-gray-200 dark:border-gray-700"
                                        />
                                    </div>
                                )}

                                {isEditMode && (
                                    <div>
                                        <label className={labelClass}>Upload New Image</label>
                                        <input
                                            type="file"
                                            accept="image/*"
                                            onChange={handleImageChange}
                                            className={`text-xs w-full ${isDarkMode ? "text-white" : ""} file:mr-2 file:py-1 file:px-2 file:rounded file:border file:border-black dark:file:border-white file:text-xs file:bg-black file:text-white dark:file:bg-white dark:file:text-black`}
                                        />
                                        <p className="text-[10px] text-gray-400 mt-1">Supported formats: JPG, PNG, JPEG</p>
                                    </div>
                                )}
                            </div>
                        </div>

                        {/* Action Buttons */}
                        <div className="flex space-x-2 pt-1">
                            <button
                                type="button"
                                onClick={handleEditToggle}
                                className="text-xs font-medium py-2 px-4 rounded border border-black dark:border-white text-black dark:text-white  hover:text-white dark:hover:bg-white dark:hover:text-black transition-colors flex items-center"
                            >
                                {isEditMode ? (
                                    <>
                                        <FaTimes color='red' className="mr-1.5" size={11} /> Cancel Editing
                                    </>
                                ) : (
                                    <>
                                        <FaEdit color='blue' className="mr-1.5" size={11} /> Edit Offer
                                    </>
                                )}
                            </button>

                            {isEditMode && (
                                <button
                                    type="submit"
                                    className="text-xs font-medium py-2 px-4 rounded bg-blue-600 text-white dark:bg-white dark:text-black hover:opacity-80 transition-all flex items-center"
                                >
                                    <FaSave className="mr-1.5" size={11} /> Save Changes
                                </button>
                            )}
                        </div>
                    </form>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ViewOffer;